import { Button, CircularProgress, Grid, InputAdornment, TextField, Typography } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import SearchIcon from "@material-ui/icons/Search";
import { Tooltip } from "@mui/material";
import Stack from "@mui/material/Stack";
import { Box } from "@mui/system";
import Field from "components/CustomFields/Field.js";
import RutField from "components/CustomFields/RutField";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import dataGridTexts from "components/DataGrid/dataGridTexts.js";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import { mapObservations } from "pages/audit/tablesUtils/Sales";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { dbGet, dbPost } from "utils/DBFetchers";
import { verifyRut } from "utils/rutValidations";
import PageTitle from "../../components/PageTitle/PageTitle";
import useStyles from "../benefits/styles";

const BLANK_FORM = {
  patientRut: "",
  patientRutVerifier: "",
  informedUnits: 0,
  discountInPesos: 0,
  presentationId: 0,
  presentationName: "",
  couponBarcodeError: "",
};

const BLANK_SALE_INFORMATION = {
  discountInPesos: 0,
  isPhysical: false,
  physicalPresentationsAvailable: [],
}
const BURNABLE_PROGRAM_ID = 1000;
const availableProductsColumns = [
  { field: "name", headerName: "Producto", width: 500 },
];
const MAX_SELECTED_PRODUCTS = 1;

export default function NewSaleBurnablePage() {
  const location = useLocation();
  const history = useHistory();
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const barcodeFromURL = new URLSearchParams(location.search).get("barcode");
  const [barcode, setBarcode] = useState(barcodeFromURL || "");
  const [form, setForm] = useState(BLANK_FORM);
  const [formErrors, setFormErrors] = useState(BLANK_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [saleInformation, setSaleInformation] = useState(BLANK_SALE_INFORMATION);
  const shouldHideBarcodeInitial = !!location.state?.barcode || !!barcodeFromURL || !!localStorage.getItem("barcode");
  const [shouldHideBarcode, setShouldHideBarcode] = useState(shouldHideBarcodeInitial);
  const [searchFilter, setSearchFilter] = useState("");
  const [discountInPesos, setDiscountInPesos] = useState(0);
  const [debouncedBarcode, setDebouncedBarcode] = useState(barcode);

  var classes = useStyles();

  const totalWidth = availableProductsColumns
    .map((x) => (x.width ? x.width : 0))
    .reduce((accumulator, currentValue) => accumulator + currentValue);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedBarcode(barcode);
    }, 600);

    return () => clearTimeout(timeout);
  }, [barcode]);

  useEffect(() => {
    if (debouncedBarcode === "") {
      setForm(BLANK_FORM);
      setSaleInformation(BLANK_SALE_INFORMATION);
      setDiscountInPesos(0);
      setFormErrors(BLANK_FORM);
      return;
    }
    getSaleInformation(debouncedBarcode);
  }, [debouncedBarcode]);

  const getSaleInformation = async (barcode) => {
    try {
      const res = await dbGet(`/burnable/information?barcode=${barcode}`);
      setForm({ 
          patientRut: res.patient_rut && res.patient_rut_verifier ? res.patient_rut + res.patient_rut_verifier : "",
          patientRutVerifier: res.patient_rut_verifier || "",
          informedUnits: 1,
          presentationId: res.presentation && res.presentation.id,
          presentationName: res.presentation && res.presentation.name,
        });
      setSaleInformation({ 
        discountInPesos: res.discount_in_pesos, // online coupon discount
        isPhysical: res.is_physical,
        physicalPresentationsAvailable: res.physical_presentations_available.map((item) => ({
          id: item.presentation.id,
          name: item.presentation.name,
          discountInPesos: item.discount_in_pesos, // physical coupon discount
        })),
      });
      setDiscountInPesos(res.discount_in_pesos ?? 0);
      if (Object.keys(res).length === 0) {
        setForm(BLANK_FORM);
      }
      setFormErrors(BLANK_FORM);
    } catch (error) {
      setForm(BLANK_FORM);
      setFormErrors({
        ...formErrors,
        couponBarcodeError: "Código de barras inexistente o ya utilizado",
      });
    }
  };


  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
    
  };

  const checkIfValidInformedUnit = () => {
    if (form.informedUnits < 1) {
      setForm({ ...form, informedUnits: 1 });
    }
  };

  const handleSubmit = () => {
    checkIfValidInformedUnit();
    setIsLoading(true);
    dbPost("/burnable/sale", {
      barcode: barcode,
      informed_units: parseInt(form.informedUnits),
      patient_rut:
        saleInformation.isPhysical && form.patientRut + form.patientRutVerifier,
      presentation_id:
        form.presentationId,
      discount: form.discountInPesos,
    })
      .then((response) => {
        snackbarOpen("success", "Venta realizada con éxito");
        window.history.replaceState(null, "",location.pathname);
        setShouldHideBarcode(false);
        
        
        setBarcode("");
        setForm(BLANK_FORM);
        setSaleInformation(BLANK_SALE_INFORMATION);
        setDiscountInPesos(0);
      })
      .catch((error) => {
        if (error instanceof Array) {
          snackbarOpen(
            "error",
            mapObservations.quemables[error[0]]?.label ||
              "Error al realizar la venta",
          );
        } else {
          snackbarOpen(
            "error",
            error.description_es || "Error al realizar la venta",
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
  if (barcodeFromURL) {
    getSaleInformation(barcodeFromURL);
  }
}, [barcodeFromURL]);


  const handleRutChange = (fieldId, isVerifier, e) => {
    if (isVerifier && e.target?.value?.length > 1) return "";
    const rut = isVerifier ? form[fieldId] : e.target.value;
    const verifier = isVerifier ? e.target.value : form[`${fieldId}Verifier`];
    const fullRut = rut + verifier;
    const isValid = verifyRut(fullRut);

    if (!verifier && 
      (rut.length == 7 || rut.length == 8)
    ) {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        [fieldId]: "Ingrese el dígito verificador en el campo respectivo",
      }));
    } else if (!isValid) {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        [fieldId]: "RUT inválido",
      }));
    } else
      setFormErrors((prevFormErrors) => ({ ...prevFormErrors, [fieldId]: "" }));
    setForm((prevForm) => ({ ...prevForm, [e.target.id]: e.target.value }));
    return fullRut;
  };

  return (
    <>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <div>
        <PageTitle title="Nueva Venta Quemable" />
      </div>
      <Grid
        style={{ display: "flex", margin: 0 }}
        container
        direction="row"
        alignItems="center"
        spacing={4}
      >
        <form autoComplete="on">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" className={classes.text}>
                Datos de la venta
              </Typography>
              <Field
                labelName="Código de barras"
                required={true}
                id="couponBarcode"
                value={barcode}
                error={formErrors.couponBarcodeError.length > 0}
                helperText={formErrors.couponBarcodeError}
                divClassType={classes.smallItems}
                fieldClassType={classes.smallFieldValidation}
                onChange={handleBarcodeChange}
                hidden={shouldHideBarcode}
              />
              {saleInformation.isPhysical ? (
                <>
                  <RutField
                    labelName="RUT Paciente"
                    id="patientRut"
                    id2="patientRutVerifier"
                    divClassType={classes.smallItems2}
                    divClassType2={classes.generalPurposeItems2}
                    fieldClassType={classes.smallFieldValidation2}
                    fieldClassType2={classes.smallFieldValidation3}
                    onChange={(e) => handleRutChange("patientRut", false, e)}
                    onChange2={(e) => handleRutChange("patientRut", true, e)}
                    value={form.patientRut}
                    value2={form.patientRutVerifier}
                    error={formErrors.patientRut.length > 0}
                    helperText={formErrors.patientRut}
                    required
                  />
                  <Grid
                    item
                    xs={12}
                    xl={6}
                    style={{
                      marginTop: "32px",
                    }}
                  >
                    <div className={classes.generalPurposeItems}>
                      <Typography className={classes.text} weight="bold">
                        Seleccionar (máximo {MAX_SELECTED_PRODUCTS}) *
                      </Typography>
                    </div>
                    <div className={classes.generalPurposeField}>
                      <TextField
                        className={classes.searchField}
                        label="Filtrar"
                        onChange={(e) => {
                          setSearchFilter(e.target.value);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {formErrors.presentationName.length > 0 && (
                        <Typography
                          className={classes.text}
                          style={{ color: "red" }}
                          weight="bold"
                          size="xl"
                        >
                          {formErrors.presentationName}
                        </Typography>
                      )}

                      <DataGrid
                        style={{ height: 360, width: totalWidth + 100 }}
                        rows={
                          searchFilter
                            ? saleInformation.physicalPresentationsAvailable.filter((x) =>
                                x.name
                                  .toLowerCase()
                                  .includes(searchFilter.toLowerCase()),
                              )
                            : saleInformation.physicalPresentationsAvailable
                        }
                        columns={availableProductsColumns}
                        showToolbar={true}
                        disableColumnSelector={true}
                        disableDensitySelector={true}
                        disableColumnMenu={true}
                        rowsPerPageOptions={[]}
                        pageSize={100}
                        localeText={dataGridTexts}
                        onSelectionModelChange={(selectedRowIds) => {
                          setForm((prev) => ({
                            ...prev,
                            presentationId: selectedRowIds.length > 0 ? selectedRowIds[0] : 0,
                          }));

                          let newDiscountInPesos = 0;
                          if (selectedRowIds.length > MAX_SELECTED_PRODUCTS) {
                            newDiscountInPesos = 0;
                          } else if (selectedRowIds.length > 0) {
                            newDiscountInPesos = saleInformation.physicalPresentationsAvailable.find((x) => x.id === selectedRowIds[0])?.discountInPesos;
                          }
                          setDiscountInPesos(newDiscountInPesos);

                          setFormErrors((prev) => ({
                            ...prev,
                            presentationName:
                              selectedRowIds.length > MAX_SELECTED_PRODUCTS
                                ? "No puedes seleccionar más de 1 producto"
                                : "",
                          }));
                        }}
                        selectionModel={saleInformation.physicalPresentationsAvailable}
                        checkboxSelection
                      />
                    </div>
                  </Grid>
                </>
              ) : ( // isPhysical is false
                <>
                  <Field
                    labelName="RUT Paciente"
                    id="patientRut"
                    value={form.patientRut}
                    divClassType={classes.smallItems}
                    fieldClassType={classes.smallFieldValidation}
                    InputProps={{ readOnly: true }}
                  />
                  <Field
                    labelName="Producto"
                    id="productName"
                    value={form.presentationName?.length > 0 ? form.presentationName : ""}
                    divClassType={classes.generalPurposeItems}
                    fieldClassType={classes.generalPurposeField}
                    InputProps={{ readOnly: true }}
                  />
                </>
              )}

              <Stack direction="row" alignItems={"center"}>
                <Field
                  labelName="Unidades"
                  id="units"
                  value={form.informedUnits? form.informedUnits : ""}
                  onChange={(e) => {
                    if (e.target.value <= 10) {
                      setForm({ ...form, informedUnits: e.target.value });
                    }
                  }}
                  onBlur={checkIfValidInformedUnit}
                  divClassType={classes.verySmallItems}
                  fieldClassType={classes.verySmallFieldValidation}
                />
                <Field
                  labelName="Descuento"
                  id="discountInPesos"
                  value={`$ ${discountInPesos * form.informedUnits}`}
                  divClassType={classes.secondVerySmallItemInRow}
                  fieldClassType={classes.verySmallFieldValidation}
                  InputProps={{ readOnly: true }}
                />
                <Stack direction="row" style={{ marginTop: "10px" }} alignItems="center">
                  <Tooltip title="El monto del descuento puede diferir del impreso en el cupón." style={{ marginRight: "10px", marginTop: "5px" }}>
                    <HelpOutlineIcon sx={{ fontSize: 20, cursor: 'pointer' }} />
                  </Tooltip>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeeba",
                      color: "#856404",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      marginTop: "10px",
                      fontSize: "10px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                    }}
                  >
                    <Typography fontWeight="bold" sx={{ marginRight: 1 }}>
                      ⚠️
                    </Typography>
                    <Typography style={{ fontSize: "0.75rem" }}>
                      Descuento expresado en pesos a partir de Agosto 2025.
                    </Typography>
                  </Box>
                </Stack>
             </Stack>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              className={classes.alignLeft}
              style={{ marginTop: "2.5%" }}
            >
              {isLoading ? (
                <CircularProgress size={26} className={classes.submitLoader} />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  type="button"
                  disabled={
                    form.product?.length === 0 ||
                    form.patientRut?.length === 0 ||
                    form.patientRutVerifier?.length === 0 ||
                    Object.values(formErrors).some((x) => x.length > 0)
                  }
                >
                  Realizar venta
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Grid>
    </>
  );
}
